/**
 * Web entry point. Bootstraps the anonymous account, the query cache and the router.
 *
 * Note the ordering: an anonymous account is created silently on first launch so the
 * learner reaches the first scored utterance without being asked for anything
 * (docs/02-user-flows.md §2).
 */

import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import '@saysooner/ui/styles.css';
import './app.css';
import { AppProvider } from './app-context.js';
import { API_BASE_URL, createClients, deviceId } from './lib/client.js';
import { ROUTE_COMPONENTS } from './route-components.js';
import { buildRouter } from './router.js';

async function bootstrap(): Promise<void> {
  const { api, queryClient } = createClients(API_BASE_URL);

  // Silent anonymous sign-in. Idempotent per device, so a reload or reinstall returns
  // the same account rather than orphaning progress.
  const existing = await api.getTokens();
  if (existing === null) {
    try {
      await api.signInAnonymously({
        deviceId: deviceId(),
        platform: 'web',
        appVersion: __APP_VERSION__,
        locale: navigator.language.startsWith('es') ? 'es' : 'en',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
    } catch {
      // The app still mounts; screens render their offline/error states rather than a
      // blank page.
    }
  }

  /**
   * Registers the device, which nothing on any client has ever done.
   *
   * `POST /devices` existed, `devices.push_token` has an index on it, and the hourly
   * re-engagement job had no device to send to — so §8's whole flow had no last mile.
   * The token is null here until the learner grants permission: a browser needs a
   * service worker and a VAPID key to receive a push, and claiming otherwise would be
   * the same empty promise as the rest of that flow. Registering anyway is what makes
   * the settings toggles mean something, and what lets the job report "decided, no
   * device" rather than silence.
   */
  try {
    await api.call('registerDevice', {
      body: {
        deviceId: deviceId(),
        platform: 'web',
        pushToken: null,
        appVersion: __APP_VERSION__,
        osVersion: navigator.userAgent.slice(0, 64),
      },
    });
  } catch {
    // Never blocks the app: a learner who cannot be notified can still learn.
  }

  let courseId: string | null = null;
  try {
    const courses = await api.call('listCourses');
    courseId = courses.courses.find((course) => course.isActive)?.id ?? null;
  } catch {
    courseId = null;
  }

  // Every route in the table is bound to a real screen; the placeholder fallback
  // in `buildRouter` exists only so tests can mount the tree in isolation.
  const router = buildRouter(ROUTE_COMPONENTS);
  const container = document.getElementById('root');
  if (container === null) throw new Error('Missing #root');

  createRoot(container).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AppProvider api={api} initialCourseId={courseId}>
          <RouterProvider router={router} />
        </AppProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

void bootstrap();
